'use client';

import { type FC, type ReactNode, useEffect, useState } from 'react';
import { cn } from '../../../utils/cn';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import { useHaptic } from '../../../hooks/useHaptic';
import { playSfx } from './sfx';
import { FaCrosshairs, FaShieldAlt, FaBolt, FaChevronUp } from '../../icons';
import {
  GRENADE_ICON_SRC, SMOKE_ICON_SRC, FLASH_ICON_SRC, KNIFE_ICON_SRC, MEDKIT_ICON_SRC,
} from '../spriteLayout';
import { bustCache } from '../render/battleAssetVersion';
import translations from './RushMid.translations';

export type Stance = 'attack' | 'maneuver' | 'defense';

export interface ActionMenuProps {
  canAct: boolean;
  hasGrenade: boolean;
  hasSmoke: boolean;
  hasFlash: boolean;
  hasMedkit: boolean;
  targetIsAlly: boolean;
  stance: Stance;
  onStance: (stance: Stance) => void;
  onKnife: () => void;
  onGrenade: () => void;
  onSmoke: () => void;
  onFlash: () => void;
  onMedkit: () => void;
}

const ORB_SRC = bustCache('/battle/am_orb.webp');
const FRAME_SRC = bustCache('/battle/am_frame.webp');

interface Theme { tint?: [string, string]; ring: string; glow: string }

const THEMES: Record<string, Theme> = {
  knife:    { ring: '#aeb4bd', glow: 'rgba(180,190,205,0.35)' },
  fire:     { tint: ['#ff6b6b', '#7d1111'], ring: '#ff6a6a', glow: 'rgba(255,70,70,0.60)' },
  attack:   { tint: ['#ff6b6b', '#7d1111'], ring: '#ff6a6a', glow: 'rgba(255,70,70,0.60)' },
  maneuver: { tint: ['#ffd166', '#7a4e0c'], ring: '#f2b84b', glow: 'rgba(240,180,74,0.55)' },
  defense:  { tint: ['#7db4ff', '#123a78'], ring: '#5aa0ff', glow: 'rgba(90,160,255,0.55)' },
  grenade:  { tint: ['#6ee08c', '#145a2c'], ring: '#57c97a', glow: 'rgba(74,222,128,0.50)' },
  frag:     { tint: ['#6ee08c', '#145a2c'], ring: '#57c97a', glow: 'rgba(74,222,128,0.50)' },
  smoke:    { tint: ['#c6cfc9', '#3a453e'], ring: '#9aa6a0', glow: 'rgba(170,185,175,0.40)' },
  flash:    { tint: ['#66e0d2', '#0d5049'], ring: '#4fd0c0', glow: 'rgba(79,208,192,0.50)' },
  medkit:   { tint: ['#7db4ff', '#123a78'], ring: '#5aa0ff', glow: 'rgba(90,160,255,0.55)' },
};

const IconImg: FC<{ src: string; size: number }> = ({ src, size }) => (
  <img src={bustCache(src)} alt="" draggable={false} style={{ width: size, height: size }} className="select-none object-contain" />
);

const Orb: FC<{
  theme: Theme; size: number; children: ReactNode; onClick: () => void;
  disabled?: boolean; arrow?: boolean; active?: boolean;
  muted?: boolean;
}> = ({ theme, size, children, onClick, disabled, arrow, active, muted }) => (
  <button
    type="button" disabled={disabled || muted} onClick={onClick}
    className={cn('relative flex items-center justify-center rounded-full disabled:cursor-default', muted && 'opacity-40 grayscale')}
    style={{ width: size, height: size, isolation: 'isolate', filter: muted ? 'drop-shadow(0 1px 1px rgba(0,0,0,0.5))' : `drop-shadow(0 1px 1px rgba(0,0,0,0.5)) drop-shadow(0 0 ${active ? 13 : 7}px ${theme.glow})` }}
  >
    <img src={ORB_SRC} alt="" draggable={false} className="pointer-events-none absolute inset-0 h-full w-full select-none" />
    {theme.tint && (
      <span
        className="pointer-events-none absolute rounded-full"
        style={{ inset: '11%', background: `radial-gradient(circle at 50% 34%, ${theme.tint[0]}, ${theme.tint[1]})`, mixBlendMode: 'color' }}
      />
    )}

    <span
      className="pointer-events-none absolute rounded-full"
      style={{ inset: '7%', boxShadow: `inset 0 0 0 2px ${theme.ring}66, inset 0 0 12px ${theme.glow}` }}
    />
    <span className="relative z-10 flex items-center justify-center">{children}</span>
    {arrow && (
      <span
        className={cn('pointer-events-none absolute left-1/2 -translate-x-1/2 transition-all', active ? '-top-3 opacity-100' : '-top-2.5 opacity-70')}
        style={{ color: theme.ring }}
      >
        <FaChevronUp size={12} />
      </span>
    )}
  </button>
);

const BarItem: FC<{
  theme: Theme; size: number; label: string; icon: ReactNode; onClick: () => void;
  disabled?: boolean; arrow?: boolean; active?: boolean; muted?: boolean;
}> = ({ theme, size, label, icon, onClick, disabled, arrow, active, muted }) => (
  <div className="flex flex-col items-center gap-1.5">
    <Orb theme={theme} size={size} onClick={onClick} disabled={disabled} arrow={arrow && !muted} active={active} muted={muted}>
      {icon}
    </Orb>
    <span className={cn('text-[11px] font-bold uppercase tracking-wide text-white/90 drop-shadow', muted && 'opacity-40')}>{label}</span>
  </div>
);

interface FanEntry { key: string; theme: Theme; label: string; icon: ReactNode; onSelect: () => void; muted?: boolean }

const Fan: FC<{ open: boolean; entries: FanEntry[]; onSelect: (fn: () => void) => void }> = ({ open, entries, onSelect }) => (
  <div
    className={cn(
      'absolute bottom-[calc(100%+14px)] left-1/2 z-20 transition-all duration-200 ease-out',
      open ? 'pointer-events-auto' : 'pointer-events-none',
    )}
    style={{ opacity: open ? 1 : 0, transform: open ? 'translateX(-50%) scale(1)' : 'translateX(-50%) translateY(10px) scale(0.9)' }}
  >
    <div className="flex items-start gap-2.5 rounded-2xl border border-white/12 bg-[#14161a] px-3 py-2 shadow-[0_8px_22px_rgba(0,0,0,0.6)]">
      {entries.map((e, i) => (
        <div
          key={e.key}
          className="flex flex-col items-center gap-1 transition-all duration-200 ease-out"
          style={{ opacity: open ? 1 : 0, transform: open ? 'scale(1)' : 'scale(0.5)', transitionDelay: open ? `${i * 40}ms` : '0ms' }}
        >
          <Orb theme={e.theme} size={46} muted={e.muted} onClick={() => { if (!e.muted) onSelect(e.onSelect); }}>
            {e.icon}
          </Orb>
          <span className={cn('whitespace-nowrap text-[8px] font-bold uppercase tracking-wide text-white/80', e.muted && 'opacity-40')}>{e.label}</span>
        </div>
      ))}
    </div>

    <div className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-x-8 border-t-8 border-x-transparent border-t-[#14161a]" />
  </div>
);

const STANCE_ICON: Record<Stance, ReactNode> = {
  attack: <FaCrosshairs size={34} className="text-white" />,
  maneuver: <FaBolt size={34} className="text-white" />,
  defense: <FaShieldAlt size={34} className="text-white" />,
};

export const ActionMenu: FC<ActionMenuProps> = ({
  canAct, hasGrenade, hasSmoke, hasFlash, hasMedkit, targetIsAlly, stance,
  onStance, onKnife, onGrenade, onSmoke, onFlash, onMedkit,
}) => {
  const { t } = useLocalTranslation(translations);
  const haptic = useHaptic();
  const [fan, setFan] = useState<'fire' | 'grenade' | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => { if (!canAct) setFan(null); }, [canAct]);
  useEffect(() => {
    if (!hint) return;
    const id = setTimeout(() => setHint(null), 1800);
    return () => clearTimeout(id);
  }, [hint]);

  const need = (side: 'ally' | 'enemy', ok: () => void) => {
    if (!canAct) return;
    if ((side === 'ally') !== targetIsAlly) {
      setHint(side === 'ally' ? t('pickAlly') : t('pickEnemy'));
      playSfx('click', 0.4);
      haptic.impact('light');
      return;
    }
    ok();
  };

  const toggle = (which: 'fire' | 'grenade') => {
    if (!canAct) return;
    playSfx('click', 0.5);
    haptic.impact('light');
    setFan((cur) => (cur === which ? null : which));
  };
  const pick = (fn: () => void) => { playSfx('click', 0.5); haptic.impact('medium'); setFan(null); fn(); };
  const leaf = (fn: () => void) => { if (!canAct) return; playSfx('click', 0.5); haptic.impact('medium'); setFan(null); fn(); };

  const stanceEntries: FanEntry[] = [
    { key: 'attack', theme: THEMES.attack, label: t('stanceAttack'), icon: <FaCrosshairs size={22} className="text-white" />, onSelect: () => onStance('attack') },
    { key: 'maneuver', theme: THEMES.maneuver, label: t('stanceManeuver'), icon: <FaBolt size={22} className="text-white" />, onSelect: () => onStance('maneuver') },
    { key: 'defense', theme: THEMES.defense, label: t('stanceDefense'), icon: <FaShieldAlt size={22} className="text-white" />, onSelect: () => onStance('defense') },
  ];

  const nadeEntries: FanEntry[] = [
    { key: 'smoke', theme: THEMES.smoke, label: t('nadeSmoke'), icon: <IconImg src={SMOKE_ICON_SRC} size={24} />, onSelect: () => need('ally', onSmoke), muted: !hasSmoke },
    { key: 'frag', theme: THEMES.frag, label: t('nadeFrag'), icon: <IconImg src={GRENADE_ICON_SRC} size={24} />, onSelect: () => need('enemy', onGrenade), muted: !hasGrenade },
    { key: 'flash', theme: THEMES.flash, label: t('flash'), icon: <IconImg src={FLASH_ICON_SRC} size={24} />, onSelect: () => need('enemy', onFlash), muted: !hasFlash },
  ];

  const hasAnyNade = hasSmoke || hasGrenade || hasFlash;

  return (
    <div className={cn('relative transition-opacity', !canAct && 'opacity-45')}>

      {fan && <div className="fixed inset-0 z-10" onClick={() => setFan(null)} />}


      {hint && (
        <div className="pointer-events-none absolute bottom-[calc(100%+22px)] left-1/2 z-30 -translate-x-1/2 whitespace-nowrap rounded-full border border-white/15 bg-black/80 px-4 py-1.5 text-[13px] font-bold text-white shadow-[0_6px_18px_rgba(0,0,0,0.6)]">
          {hint}
        </div>
      )}


      <img
        src={FRAME_SRC} aria-hidden alt="" draggable={false}
        className="pointer-events-none absolute bottom-[-4px] left-1/2 z-0 w-[96%] max-w-none -translate-x-1/2 select-none"
      />

      <div className="relative z-10 flex items-end justify-center gap-3.5 px-2 pb-0">

        <BarItem
          theme={THEMES.knife} size={62} label={t('knife')} disabled={!canAct}
          icon={<IconImg src={KNIFE_ICON_SRC} size={28} />} onClick={() => need('enemy', () => leaf(onKnife))}
        />


        <div className="relative">
          <Fan open={fan === 'fire'} entries={stanceEntries} onSelect={pick} />
          <BarItem
            theme={THEMES[stance]} size={78} label={t('stance')} disabled={!canAct}
            arrow active={fan === 'fire'}
            icon={STANCE_ICON[stance]} onClick={() => need('enemy', () => toggle('fire'))}
          />
        </div>


        <div className="relative">
          <Fan open={fan === 'grenade'} entries={nadeEntries} onSelect={pick} />
          <BarItem
            theme={THEMES.grenade} size={62} label={t('grenade')} disabled={!canAct} muted={!hasAnyNade}
            arrow active={fan === 'grenade'}
            icon={<IconImg src={GRENADE_ICON_SRC} size={28} />} onClick={() => { if (hasAnyNade) toggle('grenade'); }}
          />
        </div>


        <BarItem
          theme={THEMES.medkit} size={62} label={t('medkit')} disabled={!canAct} muted={!hasMedkit}
          icon={<IconImg src={MEDKIT_ICON_SRC} size={28} />} onClick={() => leaf(onMedkit)}
        />
      </div>
    </div>
  );
};

export default ActionMenu;
